import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Typography,
  Stack,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Fab,
  Icon,
  Box,
  CircularProgress,
  Paper,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { Description, Gavel, Delete } from "@mui/icons-material";
import { getPolicies, deletePolicy } from "services/api";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";
import AddFloatingButton from "components/AddFloatingButton";
import SnackbarWrapper from "components/Snackbar";
import { useAuth } from "context/authContext";
import { hasEditAccess } from "services/utils";

const PolicyViewer = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { modules, employee } = useAuth();
  const [canEdit, setCanEdit] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteloading] = useState(false);
  // const [snackbarOpen, setSnackbarOpen] = useState(false);
  // const [snackbarMessage, setSnackbarMessage] = useState("");
  // const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const snackbarRef = useRef();

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    fetchPolicies();
    setCanEdit(hasEditAccess(modules, "Policies", employee));
  }, []);

  const fetchPolicies = async () => {
    setLoading(true);
    const result = await getPolicies();
    if (result !== "error") {
      setPolicies(result);
    }
    setLoading(false);
  };

  const handleSelectPolicy = (id) => {
    navigate("/policy", {
      state: { id },
    });
  };

  const handleDeleteClick = (policyId, event) => {
    event.stopPropagation();
    setPolicyToDelete(policyId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteloading(true);
    const result = await deletePolicy(policyToDelete);
    if (result !== "error") {
      snackbarRef.current?.showSnackbar(t("admin.policies.snackbar.deleteSuccess"), "success");
      await fetchPolicies();
    } else {
      snackbarRef.current?.showSnackbar(t("admin.policies.snackbar.deleteError"), "error");
    }
    setDeleteDialogOpen(false);
    setDeleteloading(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPolicyToDelete(null);
  };

  if (loading && policies.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={1}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, pb: 8 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Gavel fontSize="large" color="primary" />
        <Typography variant="h4" fontWeight="bold">
          {t("admin.policies.title")}
        </Typography>
      </Stack>
      {policies?.length > 0 ? (
        <Typography variant="body1" mb={2}>
          {t("admin.policies.selectPrompt")}
        </Typography>
      ) : (
        <Typography variant="body1" mb={2}>
          {t("admin.policies.noPolicies")}
        </Typography>
      )}
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          maxHeight: "70vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "0.4em",
          },
          "&::-webkit-scrollbar-track": {
            boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
            webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary.main,
            borderRadius: 2,
          },
        }}
      >
        <List disablePadding>
          {policies.map((policy, index) => {
            const id = policy.split(",")[0];
            const title = policy.split(",")[1];

            return (
              <Box key={id}>
                <ListItemButton
                  onClick={() => handleSelectPolicy(id)}
                  sx={{
                    py: 2,
                    px: 3,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Description color="primary" sx={{ mr: 2, fontSize: "1.2rem" }} />
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {title}
                      </Typography>
                    }
                    secondaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                    }}
                  />
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    disabled={!canEdit}
                    onClick={(e) => handleDeleteClick(id, e)}
                    color="error"
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.error.light,
                      },
                    }}
                  >
                    <Delete />
                  </IconButton>
                </ListItemButton>
                {index < policies.length - 1 && <Divider variant="middle" component="li" />}
              </Box>
            );
          })}
        </List>
      </Paper>
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("admin.policies.deleteDialog.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("admin.policies.deleteDialog.description")}
          </DialogContentText>
        </DialogContent>
        {deleteLoading ? (
          <Box display="flex" justifyContent="center" p={1}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <DialogActions>
            <Button onClick={handleCancelDelete} color="secondary">
              {t("admin.policies.deleteDialog.cancel")}
            </Button>
            <MDButton onClick={handleConfirmDelete} color="error">
              {t("admin.policies.deleteDialog.confirm")}
            </MDButton>
          </DialogActions>
        )}
      </Dialog>

      {/* Add Policy Button */}
      <MDBox sx={{ position: "fixed", bottom: 20, right: 20 }}>
        <Fab
          color="primary"
          aria-label="add"
          disabled={!canEdit}
          onClick={() => handleSelectPolicy()}
        >
          <Icon>add</Icon>
        </Fab>
      </MDBox>
    </Container>
  );
};

export default PolicyViewer;
